import { connectHits } from "instantsearch.js/es/connectors";
import type { Hit } from "instantsearch.js";
import type { SearchResults } from "algoliasearch-helper";
import {
    isLandingExcerptPathname,
    resolveLandingDisplayPageFromUrl,
} from "../../services/algoliaRouting.core";
import { scheduleVehicleCardCarouselInit } from "./vehicle-card";
import { runAfterFrame } from "./helpers";
import {
    shouldKeepMarketplaceSsrGrid as shouldKeepSsrGridForRoot,
    ssrGridStillMatches,
    vehicleHitIdsKey,
} from "./reconciled-hits-logic";
import { isPromoCard, PROMO_ITEM_CLASS } from "../promo-cards";

export let marketplaceHitsReconciled = false;
export let marketplaceHitsSsrRemoved = false;
/** Véhicules de la grille SSR au moment de la réconciliation. */
let reconciledHitIdsKey: string | null = null;

type MarketplaceHit = Hit<Record<string, unknown>>;

interface ReconciledHitsTemplates {
    item: (hit: Record<string, unknown>) => string;
    empty: () => string;
}

interface ReconciledHitsCssClasses {
    root: string;
    list: string;
    item: string;
}

export interface ReconciledMarketplaceHitsOptions {
    container: string;
    /** Must match `data-ssr-query-key` on `#marketplace-hits-ssr` (all InstantSearch mount modes). */
    ssrQueryKey?: string | null;
    cssClasses: ReconciledHitsCssClasses;
    transformItems?: (
        items: MarketplaceHit[],
        ctx: { results: SearchResults },
    ) => MarketplaceHit[];
    templates: ReconciledHitsTemplates;
    onReconciled?: () => void;
    onSsrRemoved?: () => void;
}

function canReconcileWithSsr(
    results: SearchResults | null | undefined,
    hits: MarketplaceHit[],
    configQueryKey?: string | null,
): boolean {
    const ssrRoot = document.getElementById("marketplace-hits-ssr");

    return shouldKeepSsrGridForRoot(
        ssrRoot,
        results,
        hits,
        configQueryKey,
        marketplaceHitsSsrRemoved,
    );
}

/**
 * Keep Blade SSR visible until InstantSearch pagination matches the 1-based `?page=` URL.
 * Prevents a flash of page-1 client hits when routing hydrates after the first response.
 */
function shouldDeferClientHitsUntilPageSync(
    results: SearchResults | null | undefined,
): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    if (!isLandingExcerptPathname(window.location.pathname)) {
        return false;
    }

    const expectedClientPage = resolveLandingDisplayPageFromUrl() - 1;
    const clientPage = results?.page ?? 0;

    return clientPage !== expectedClientPage;
}

/** Drops SSR `content-visibility` deferral so hover shadows paint correctly. */
function markSsrGridHydrated(): void {
    document
        .getElementById("marketplace-hits-ssr")
        ?.classList.add("marketplace-hits-ssr--hydrated");
}

function removeSsrGrid(onSsrRemoved?: () => void): void {
    if (marketplaceHitsSsrRemoved) {
        return;
    }

    document.getElementById("marketplace-hits-ssr")?.remove();
    marketplaceHitsSsrRemoved = true;
    onSsrRemoved?.();
}

/** Cards rendered synchronously; the rest lands after the next paint. */
const CLIENT_HITS_FIRST_CHUNK = 8;

/** Invalidates the pending below-fold chunk when a newer render lands first. */
let clientHitsRenderToken = 0;

function renderClientHits(
    container: HTMLElement,
    items: MarketplaceHit[],
    cssClasses: ReconciledHitsCssClasses,
    templates: ReconciledHitsTemplates,
): void {
    const token = ++clientHitsRenderToken;

    if (items.length === 0) {
        container.innerHTML = templates.empty();
        return;
    }

    const itemHtml = (hit: MarketplaceHit): string =>
        `<li class="ais-Hits-item ${cssClasses.item}${
            isPromoCard(hit) ? ` ${PROMO_ITEM_CLASS}` : ""
        }">${templates.item(
            hit,
        )}</li>`;

    // Two-phase render: hydrating all 48 card templates in one task blocks
    // the main thread ~250ms on throttled mobile (INP input delay for the
    // next tap). Only the above-fold chunk renders synchronously.
    const firstChunk = items.slice(0, CLIENT_HITS_FIRST_CHUNK);
    const rest = items.slice(CLIENT_HITS_FIRST_CHUNK);

    container.innerHTML = `
        <div class="ais-Hits ${cssClasses.root}">
            <ol class="ais-Hits-list ${cssClasses.list}">${firstChunk
                .map(itemHtml)
                .join("")}</ol>
        </div>
    `;

    if (rest.length === 0) {
        return;
    }

    runAfterFrame(() => {
        if (token !== clientHitsRenderToken) return;
        const list = container.querySelector<HTMLElement>(".ais-Hits-list");
        if (!list) return;
        list.insertAdjacentHTML("beforeend", rest.map(itemHtml).join(""));
        scheduleVehicleCardCarouselInit();
    });
}

/**
 * Keeps the Blade SSR hits grid when the first InstantSearch response matches it.
 * Replaces the grid only when the query diverges (filters, sort, pagination, etc.).
 */
export function createReconciledMarketplaceHitsWidget(
    options: ReconciledMarketplaceHitsOptions,
) {
    const renderHits = connectHits((renderOptions) => {
        const { hits, results } = renderOptions;
        const container = document.querySelector<HTMLElement>(
            options.container,
        );
        if (!container || !results) {
            return;
        }

        const items = options.transformItems
            ? options.transformItems(hits, { results })
            : hits;

        if (
            canReconcileWithSsr(results, items, options.ssrQueryKey) &&
            ssrGridStillMatches(reconciledHitIdsKey, items)
        ) {
            if (!marketplaceHitsReconciled) {
                marketplaceHitsReconciled = true;
                reconciledHitIdsKey = vehicleHitIdsKey(items);
                markSsrGridHydrated();
                scheduleVehicleCardCarouselInit();
                options.onReconciled?.();
            }
            return;
        }

        if (shouldDeferClientHitsUntilPageSync(results)) {
            return;
        }

        removeSsrGrid(options.onSsrRemoved);
        renderClientHits(
            container,
            items,
            options.cssClasses,
            options.templates,
        );
    });

    return renderHits({});
}
